import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { PendingRequestsService } from './pending-requests.service';

const SAFETY_TIMEOUT_MS = 30_000;
const SAFETY_POLL_INTERVAL_MS = 2_000;
@Injectable({
  providedIn: 'root',
})
export class GlobalLoaderService {
  private readonly _document = inject(DOCUMENT);
  private readonly _pendingRequestsService = inject(PendingRequestsService);

  private _isLoaderActiveCount = signal(0);
  isLoaderActive = computed(() => this._isLoaderActiveCount() > 0);

  private _loaderElement: HTMLElement | null = null;
  private _messageElement: HTMLElement | null = null;
  private _safetyTimer: ReturnType<typeof setTimeout> | null = null;
  private _safetyPollTimer: ReturnType<typeof setInterval> | null = null;

  show(message?: string) {
    this._isLoaderActiveCount.update((count) => count + 1);

    if (!this._loaderElement) {
      this._createLoader();
    }

    this._setMessage(message);
    this._updateDOM();
    this._restartSafetyTimer();
  }

  hide() {
    this._isLoaderActiveCount.update((count) => Math.max(0, count - 1));
    this._updateDOM();

    if (!this.isLoaderActive()) {
      this._clearSafetyTimers();
      this._setMessage();
    }
  }

  /**
   * Wymusza natychmiastowe ukrycie loadera, resetując licznik do zera.
   * Używane jako mechanizm awaryjny (safety reset).
   */
  forceHide() {
    this._isLoaderActiveCount.set(0);
    this._clearSafetyTimers();
    this._setMessage();
    this._updateDOM();
  }

  private _setMessage(message?: string) {
    if (!this._messageElement) return;
    this._messageElement.textContent = message ?? 'Ładowanie... ';
  }

  private _restartSafetyTimer() {
    this._clearSafetyTimers();

    this._safetyTimer = setTimeout(() => {
      // Jeśli loader z jakiegokolwiek powodu wciąż tam jest,
      // np. strzał z MapLibre utknął lub zapytanie jest baaaaardzo długie
      if (!this.isLoaderActive()) return;

      if (!this._pendingRequestsService.hasPendingRequests()) {
        console.warn(
          `[GlobalLoaderService] Loader aktywny ponad ${SAFETY_TIMEOUT_MS / 1000}s bez powiązanych zapytań API – wymuszam reset. ` +
            `Pozostały licznik: ${this._isLoaderActiveCount()}`,
        );
        this.forceHide();
        return;
      }

      console.warn(
        `[GlobalLoaderService] Zapytania API wciąż nie odpowiedziały po ${SAFETY_TIMEOUT_MS / 1000}s. ` +
          `Oczekujemy na ich zakończenie...`,
      );

      // Skoro wciąż są aktywne zapytania, badamy to cyklicznie
      // i wymuszamy usunięcie loadera, dopiero GDY zapytania się zakończą
      this._safetyPollTimer = setInterval(() => {
        if (!this.isLoaderActive()) {
          this._clearSafetyTimers();
          return;
        }

        if (!this._pendingRequestsService.hasPendingRequests()) {
          console.warn(
            '[GlobalLoaderService] Zapytania ustąpiły (lub przerwane). Bezpieczny reset loadera po długim czasie.',
          );
          this.forceHide();
        }
      }, SAFETY_POLL_INTERVAL_MS);
    }, SAFETY_TIMEOUT_MS);
  }

  private _clearSafetyTimers() {
    if (this._safetyTimer !== null) {
      clearTimeout(this._safetyTimer);
      this._safetyTimer = null;
    }
    if (this._safetyPollTimer !== null) {
      clearInterval(this._safetyPollTimer);
      this._safetyPollTimer = null;
    }
  }

  private _createLoader() {
    const loader = this._document.createElement('div');
    loader.className = 'global-loader';

    loader.innerHTML = `
      <div class="loader-wrapper">
        <svg class="loader" viewBox="0 0 50 50">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="3" />
        </svg>
        <span class="loader-message">Ładowanie... </span>
      </div>
    `;

    this._document.body.appendChild(loader);
    this._loaderElement = loader;
    this._messageElement = loader.querySelector('.loader-message');
  }

  private _updateDOM() {
    if (!this._loaderElement) return;
    this._loaderElement.style.display = this.isLoaderActive() ? 'flex' : 'none';
  }
}
